import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import {compose} from "mu-lambda";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Title} from "@khanacademy/wonder-blocks-typography";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import Log, {Errors} from "@khan/logging";
import {mediaQueries} from "@khan/shared-styles";

import PageRoot from "#app-shell-package/page-root.tsx";
import ApolloQuery from "#data-access/gql/apollo-query.tsx";
import PageDisplay from "#data-access/page-display.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedSpinner from "#shared-components-package/deprecated-spinner.tsx";
import {formatTimeDisplay} from "#utils/time-package/main.ts";

import {withStrings} from "./locales/simple-transcript/load-strings.tsx";

export type SubtitlesType = {
    startTime: number;
    text: string;
};

// TODO: (LP-10833) Add a button to the article page with the following string
export const transcriptButtonText = () => t`See video transcript`;

type Props = {
    videoId: string;
} & WithLocationProps;

type VideoData = {
    videoById?: {
        id?: string;
        title?: string;
        augmentedTranscript?: string;
        subtitles: Array<SubtitlesType>;
    };
};

type Variables = {
    videoId: string;
};

export const TRANSCRIPT_QUERY: GqlOperation<VideoData, Variables> = gqlOp(gql`
    query TranscriptQuery($videoId: String!) {
        videoById(contentId: $videoId) {
            id
            title
            augmentedTranscript
            subtitles {
                startTime
                text
            }
        }
    }
`);

const formatSubtitles = (subtitle: SubtitlesType, index: number) => {
    const subtitleText = subtitle.text.replace(/\n/g, " ");
    return (
        <li key={index} className={css(styles.row)}>
            <span className={css(styles.time)}>
                {formatTimeDisplay(subtitle.startTime / 1000)}
            </span>
            <span className={css(styles.subtitleContent)}>{subtitleText}</span>
        </li>
    );
};

class SimpleTransript extends React.Component<Props> {
    render(): React.ReactNode {
        const {videoId} = this.props;
        const videoNotFound = t`Video not found`;
        if (videoId == null) {
            return <PageRoot>{videoNotFound}</PageRoot>;
        }
        return (
            <PageRoot>
                <ApolloQuery
                    query={TRANSCRIPT_QUERY}
                    variables={{videoId}}
                    fetchPolicy="cache-and-network"
                >
                    {(result) => {
                        if (result.loading) {
                            return <DeprecatedSpinner />;
                        }
                        const videoById = result.data?.videoById;
                        let subtitles;
                        if (videoById && videoById.subtitles) {
                            subtitles =
                                videoById?.subtitles.length > 0
                                    ? videoById.subtitles
                                    : undefined;
                        }
                        const transcriptTitle =
                            videoById?.title || videoNotFound;

                        const transcriptText =
                            videoById?.augmentedTranscript ||
                            subtitles ||
                            t`Transcript not found`;

                        /// error logging
                        if (videoById?.title === undefined) {
                            Log.error(
                                `video not found for video ID: ${videoId}`,
                                Errors.Internal,
                            );
                        } else if (
                            videoById?.subtitles.length === 0 &&
                            !videoById?.augmentedTranscript
                        ) {
                            Log.error(
                                `transcript not found for video ID: ${videoId}`,
                                Errors.Internal,
                            );
                        }
                        let textContent;
                        let titleClass;
                        if (typeof transcriptText !== "string") {
                            textContent = (
                                <div>
                                    <ul>
                                        {transcriptText.map(formatSubtitles)}
                                    </ul>
                                </div>
                            );
                            titleClass = styles.subtitleTitle;
                        } else {
                            textContent = <div>{transcriptText}</div>;
                            titleClass = styles.augmentedTitle;
                        }
                        return (
                            <PageDisplay
                                settings={{
                                    title: transcriptTitle,
                                }}
                            >
                                <View style={styles.sectionContainer}>
                                    <View style={titleClass}>
                                        <Title>
                                            {videoById?.title
                                                ? t`Transcript for ${transcriptTitle}`
                                                : transcriptTitle}
                                        </Title>
                                    </View>
                                    <Strut size={24} />
                                    {textContent}
                                </View>
                            </PageDisplay>
                        );
                    }}
                </ApolloQuery>
            </PageRoot>
        );
    }
}

const styles = StyleSheet.create({
    sectionContainer: {
        margin: `${spacing.large_24}px auto`,
        maxWidth: 768,
        padding: spacing.large_24,
        whiteSpace: "pre-wrap",
        fontSize: "16px",
    },
    time: {
        display: "inline-block",
        color: color.offBlack64,
        marginRight: spacing.medium_16,
        minWidth: 50,
    },
    row: {
        position: "relative",
        marginBottom: spacing.xSmall_8,
        textDecoration: "none",
    },
    subtitleContent: {
        display: "inline-block",
        [mediaQueries.xs]: {
            maxWidth: 200,
        },
    },
    subtitleTitle: {
        marginBottom: spacing.large_24,
        [mediaQueries.mdOrLarger]: {
            marginLeft: spacing.xxxLarge_64,
        },
    },
    augmentedTitle: {
        marginBottom: spacing.large_24,
    },
});
type ExportProps = WithoutLocation<
    JSX.LibraryManagedAttributes<
        typeof SimpleTransript,
        React.ComponentProps<typeof SimpleTransript>
    >
>;

export default compose(
    withStrings,
    withLocation,
)(SimpleTransript) as React.ComponentType<ExportProps>;
